import { Drawer, Form, Input, Button, Switch, Space, message, Typography, Select } from 'antd';
import { useEffect, useMemo } from 'react';
import {
  useCreatePromptTemplateMutation,
  useGetPromptTemplateByIdQuery,
  useUpdatePromptTemplateMutation,
} from '../../../store/stylist/stylistApi';
import { useAppSelector } from '../../../store/store';
import { selectCurrentTemplate } from '../../../store/stylist/slice';

const { TextArea } = Input;
const { Option } = Select;

interface UpsertPromptTemplateDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  key?: string;
  name: string;
  description: string;
  content: string;
  template_type:
    | 'standard_workflow_prompt'
    | 'occasion_workflow_prompt'
    | 'image_generation_prompt';
  is_active: boolean;
  model_config?: string;
  metadata?: string;
}

const UpsertPromptTemplateDrawer = ({ open, onClose }: UpsertPromptTemplateDrawerProps) => {
  const [form] = Form.useForm<FormValues>();
  const currentTemplate = useAppSelector(selectCurrentTemplate);

  const { data: fullTemplate } = useGetPromptTemplateByIdQuery(currentTemplate?.id ?? 0, {
    skip: !currentTemplate?.id || !open,
  });

  const templateForForm = useMemo(
    () => fullTemplate || currentTemplate,
    [currentTemplate, fullTemplate],
  );

  const [createPromptTemplate, { isLoading: isCreating }] = useCreatePromptTemplateMutation();
  const [updatePromptTemplate, { isLoading: isUpdating }] = useUpdatePromptTemplateMutation();

  const isEditing = !!currentTemplate;
  const isLoading = isCreating || isUpdating;

  // Set form values when template is available
  useEffect(() => {
    if (templateForForm && open) {
      form.setFieldsValue({
        key: templateForForm.key,
        name: templateForForm.name,
        description: templateForForm.description,
        content: templateForForm.content,
        template_type: templateForForm.template_type,
        is_active: templateForForm.is_active,
        model_config: fullTemplate?.model_config
          ? JSON.stringify(fullTemplate.model_config, null, 2)
          : undefined,
        metadata: fullTemplate?.metadata
          ? JSON.stringify(fullTemplate.metadata, null, 2)
          : undefined,
      });
    }
  }, [templateForForm, form, open]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async (values: FormValues) => {
    try {
      const parseJson = (label: string, raw?: string) => {
        if (!raw || raw.trim() === '') return undefined;
        try {
          return JSON.parse(raw);
        } catch (error) {
          message.error(`Invalid JSON in ${label}`);
          throw error;
        }
      };

      const model_config = parseJson('Model Config', values.model_config);
      const metadata = parseJson('Metadata', values.metadata);

      if (isEditing && currentTemplate) {
        await updatePromptTemplate({
          id: currentTemplate.id,
          key: values.key,
          name: values.name,
          description: values.description,
          content: values.content,
          template_type: values.template_type,
          is_active: values.is_active,
          model_config,
          metadata,
        }).unwrap();
        message.success('Prompt template updated successfully!');
      } else {
        await createPromptTemplate({
          key: values.key,
          name: values.name,
          description: values.description,
          content: values.content,
          template_type: values.template_type,
          is_active: values.is_active,
          model_config,
          metadata,
        }).unwrap();
        message.success('Prompt template created successfully!');
      }

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Error saving prompt template:', error);
      message.error(
        `Failed to ${isEditing ? 'update' : 'create'} prompt template. Please try again.`,
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const getDrawerTitle = () => {
    if (isEditing) {
      return currentTemplate ? `Edit: ${currentTemplate.name}` : 'Edit Prompt Template';
    }
    return 'Create New Prompt Template';
  };

  return (
    <Drawer
      title={getDrawerTitle()}
      open={open}
      onClose={handleCancel}
      width={600}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={isLoading}>
            {isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* <Form.Item name="key" label="Template Key">
          <Input placeholder="auto-generated if left blank" showCount maxLength={200} />
        </Form.Item> */}

        <Form.Item
          name="template_type"
          label="Template Type"
          rules={[{ required: true, message: 'Please select a template type' }]}
        >
          <Select placeholder="Select template type">
            <Option value="standard_workflow_prompt">Standard Workflow Prompt</Option>
            <Option value="occasion_workflow_prompt">Occasion Workflow Prompt</Option>
            <Option value="image_generation_prompt">Image Generation Prompt</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="name"
          label="Template Name"
          rules={[
            { required: true, message: 'Please enter a template name' },
            {
              min: 3,
              message: 'Template name must be at least 3 characters',
            },
            { max: 100, message: 'Template name cannot exceed 100 characters' },
          ]}
        >
          <Input placeholder="Enter template name" showCount maxLength={100} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Please enter a description' },
            {
              min: 10,
              message: 'Description must be at least 10 characters',
            },
            { max: 500, message: 'Description cannot exceed 500 characters' },
          ]}
        >
          <TextArea placeholder="Enter template description" rows={3} showCount maxLength={500} />
        </Form.Item>

        <Form.Item
          name="content"
          label="Template Content"
          rules={[
            { required: true, message: 'Please enter template content' },
            {
              min: 20,
              message: 'Template content must be at least 20 characters',
            },
          ]}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Use variables like {'{user_input}'}, {'{context}'}, etc. for dynamic content
            </Typography.Text>
          }
        >
          <TextArea placeholder="Enter your prompt template content here..." rows={8} showCount />
        </Form.Item>

        <Form.Item name="model_config" label="Model Config (JSON)">
          <TextArea placeholder='{"temperature": 0.7}' rows={4} />
        </Form.Item>

        <Form.Item name="metadata" label="Metadata (JSON)">
          <TextArea placeholder='{"owner": "stylist"}' rows={4} />
        </Form.Item>

        <Form.Item
          name="is_active"
          label="Active Status"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default UpsertPromptTemplateDrawer;
